import axios from '@/utils/axios'

import { defineStore } from 'pinia'
import { getSavedState, clearAuth, saveState } from '@/utils/helper'
import { useNotificationStore } from './notification'
import { useDonorStore } from './donor'
import { useFundingSourceStore } from '@/stores/funding-source'
import * as Sentry from '@sentry/vue'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    accessToken: getSavedState('auth.Token'),
    loginModalRef: null
  }),
  getters: {
    loggedIn() {
      return !!this.getVerifiedTokenClaims
    },
    getLoginModalRef(state) {
      return state.loginModalRef
    },
    viewAsDonor() {
      return this.getVerifiedTokenClaims?.proxy
    },
    getToken(state) {
      return state.accessToken
    },
    /**
     * Parse the claims from the access token and return them with no additional checks.
     * @returns {object|null}
     */
    getTokenClaims() {
      const token = this.getToken
      if (!token) {
        return null
      }

      const tokenParts = token.split('.')
      return JSON.parse(window.atob(tokenParts[1]))
    },
    /**
     * Parse the claims from the access token, validate them, and log out if the token is invalid.
     * @returns {object|null}
     */
    getVerifiedTokenClaims() {
      const claims = this.getTokenClaims
      if (!claims) {
        return null
      }

      const now = Math.floor(Date.now() / 1000)

      if (claims.exp && claims.exp < now) {
        this.logOut()
        return null
      }

      return claims
    }
  },
  actions: {
    setToken(token) {
      this.accessToken = token
      saveState('auth.Token', token)
    },
    setLoginModalRef(loginRef) {
      this.loginModalRef = loginRef
    },
    async login({ email, password, domain }) {
      const analyticsProps = { email: email }
      window.analytics.track('Clicked On Login', analyticsProps)

      const formData = new FormData()
      formData.append('email', email)
      formData.append('password', password)
      formData.append('domain', domain)
      return axios
        .post('/login/email', formData)
        .then(async (Response) => {
          const res = Response.data
          this.setToken(res.token)

          window.analytics.identify({
            email,
            domain
          })

          return res
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(notification.getNotification)
        })
    },
    async signup({
      email,
      password,
      firstName,
      lastName,
      countryCode,
      phoneNumber,
      charityId,
      recaptchaToken,
      automaticRegistration = false,
      saveToken = true,
      enableNewsletter = false
    }) {
      const formData = new FormData()
      formData.append('charity_id', charityId)
      formData.append('email', email)
      formData.append('password', password)
      formData.append('first_name', firstName)
      formData.append('last_name', lastName)
      formData.append('enable_newsletter', enableNewsletter)
      formData.append('recaptcha_token', recaptchaToken)
      formData.append('automatic_registration', automaticRegistration)

      if (phoneNumber) {
        formData.append('country_code', countryCode)
        formData.append('phone_number', phoneNumber)
      }

      return axios
        .post('/signup', formData)
        .then((Response) => {
          const res = Response.data

          if (saveToken) {
            this.setToken(res.token)
          }

          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(notification.getNotification?.message)
        })
    },
    logOut() {
      Sentry.getCurrentScope().clear()
      Sentry.getCurrentScope().setUser(null)

      const donor = useDonorStore()
      const fundingSourceStore = useFundingSourceStore()
      this.accessToken = null
      donor.setDonorDetail(null)
      fundingSourceStore.setAllFundingSources([])
      clearAuth()
    },
    async resetPassword({ email, charity_id }) {
      const formData = new FormData()
      formData.append('email', email)
      formData.append('charity_id', charity_id)

      return axios
        .post('/account/password/reset', formData)
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject((Error.response?.data && Error.response.data?.error) || 'Error')
        })
    },
    async recoverPassword({ token, password }) {
      const formData = new FormData()
      formData.append('token', token)
      formData.append('password', password)

      return axios
        .post('/account/password', formData)
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject((Error.response?.data && Error.response.data?.error) || 'Error')
        })
    }
  }
})
